import type { Spell } from '@character-creator/types';

export const level3Spells: Spell[] = [
  {
    id: 'animate-dead',
    name: 'Animate Dead',
    level: 3,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 minute',
    range: '10 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a drop of blood, a piece of flesh, and a pinch of bone dust' },
    duration: 'Instantaneous',
    concentration: false,
    description: "This spell creates an undead servant. Choose a pile of bones or a corpse of a Medium or Small humanoid within range. Your spell imbues the target with a foul mimicry of life, raising it as an undead creature. The target becomes a skeleton if you chose bones or a zombie if you chose a corpse. On each of your turns, you can use a bonus action to mentally command any creature you made with this spell if the creature is within 60 feet of you.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, you animate or reassert control over two additional undead creatures for each slot level above 3rd.",
    classes: ['cleric', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'beacon-of-hope',
    name: 'Beacon of Hope',
    level: 3,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "This spell bestows hope and vitality. Choose any number of creatures within range. For the duration, each target has advantage on Wisdom saving throws and death saving throws, and regains the maximum number of hit points possible from any healing.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'bestow-curse',
    name: 'Bestow Curse',
    level: 3,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "You touch a creature, and that creature must succeed on a Wisdom saving throw or become cursed for the duration of the spell. When you cast this spell, choose the nature of the curse from several options including disadvantage on ability checks, disadvantage on attack rolls, wasting turns, or taking extra necrotic damage.",
    higherLevel: "If you cast this spell using a spell slot of 4th level or higher, the duration is concentration, up to 10 minutes. If you use a spell slot of 5th level or higher, the duration is 8 hours. If you use a spell slot of 7th level or higher, the duration is 24 hours. If you use a 9th level spell slot, the spell lasts until it is dispelled.",
    classes: ['bard', 'cleric', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'blink',
    name: 'Blink',
    level: 3,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: false,
    description: "Roll a d20 at the end of each of your turns for the duration of the spell. On a roll of 11 or higher, you vanish from your current plane of existence and appear in the Ethereal Plane. At the start of your next turn, and when the spell ends if you are on the Ethereal Plane, you return to an unoccupied space of your choice that you can see within 10 feet of the space you vanished from.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'call-lightning',
    name: 'Call Lightning',
    level: 3,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '10 minutes',
    concentration: true,
    description: "A storm cloud appears in the shape of a cylinder that is 10 feet tall with a 60-foot radius, centered on a point you can see within range directly above you. The spell fails if you can't see a point in the air where the storm cloud could appear. When you cast the spell, choose a point you can see under the cloud. A bolt of lightning flashes down from the cloud to that point. Each creature within 5 feet of that point must make a Dexterity saving throw. A creature takes 3d10 lightning damage on a failed save, or half as much damage on a successful one.",
    higherLevel: "When you cast this spell using a spell slot of 4th or higher level, the damage increases by 1d10 for each slot level above 3rd.",
    classes: ['druid'],
    source: 'srd-5.1',
  },
  {
    id: 'clairvoyance',
    name: 'Clairvoyance',
    level: 3,
    school: 'divination',
    ritual: false,
    castingTime: '10 minutes',
    range: '1 mile',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a focus worth at least 100 gp, either a jeweled horn for hearing or a glass eye for seeing', materialCost: 100 },
    duration: '10 minutes',
    concentration: true,
    description: "You create an invisible sensor within range in a location familiar to you (a place you have visited or seen before) or in an obvious location that is unfamiliar to you (such as behind a door, around a corner, or in a grove of trees). The sensor remains in place for the duration, and it can't be attacked or otherwise interacted with. When you cast the spell, you choose seeing or hearing. You can use the chosen sense through the sensor as if you were in its space.",
    classes: ['bard', 'cleric', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'conjure-animals',
    name: 'Conjure Animals',
    level: 3,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: true,
    description: "You summon fey spirits that take the form of beasts and appear in unoccupied spaces that you can see within range. Choose one of the following options for what appears: One beast of challenge rating 2 or lower, Two beasts of challenge rating 1 or lower, Four beasts of challenge rating 1/2 or lower, or Eight beasts of challenge rating 1/4 or lower.",
    higherLevel: "When you cast this spell using certain higher-level spell slots, you choose one of the summoning options above, and more creatures appear: twice as many with a 5th-level slot, three times as many with a 7th-level slot, and four times as many with a 9th-level slot.",
    classes: ['druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'counterspell',
    name: 'Counterspell',
    level: 3,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 reaction, which you take when you see a creature within 60 feet of you casting a spell',
    range: '60 feet',
    components: { verbal: false, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You attempt to interrupt a creature in the process of casting a spell. If the creature is casting a spell of 3rd level or lower, its spell fails and has no effect. If it is casting a spell of 4th level or higher, make an ability check using your spellcasting ability. The DC equals 10 + the spell's level. On a success, the creature's spell fails and has no effect.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, the interrupted spell has no effect if its level is less than or equal to the level of the spell slot you used.",
    classes: ['sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'create-food-and-water',
    name: 'Create Food and Water',
    level: 3,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You create 45 pounds of food and 30 gallons of water on the ground or in containers within range, enough to sustain up to fifteen humanoids or five steeds for 24 hours. The food is bland but nourishing, and spoils if uneaten after 24 hours. The water is clean and doesn't go bad.",
    classes: ['cleric', 'paladin'],
    source: 'srd-5.1',
  },
  {
    id: 'daylight',
    name: 'Daylight',
    level: 3,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: false,
    description: "A 60-foot-radius sphere of light spreads out from a point you choose within range. The sphere is bright light and sheds dim light for an additional 60 feet. If you chose a point on an object you are holding or one that isn't being worn or carried, the light shines from the object and moves with it. Completely covering the affected object with an opaque object, such as a bowl or a helm, blocks the light.",
    classes: ['cleric', 'druid', 'paladin', 'ranger', 'sorcerer'],
    source: 'srd-5.1',
  },
  {
    id: 'dispel-magic',
    name: 'Dispel Magic',
    level: 3,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "Choose one creature, object, or magical effect within range. Any spell of 3rd level or lower on the target ends. For each spell of 4th level or higher on the target, make an ability check using your spellcasting ability. The DC equals 10 + the spell's level. On a successful check, the spell ends.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, you automatically end the effects of a spell on the target if the spell's level is equal to or less than the level of the spell slot you used.",
    classes: ['bard', 'cleric', 'druid', 'paladin', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'fear',
    name: 'Fear',
    level: 3,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (30-foot cone)',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a white feather or the heart of a hen' },
    duration: '1 minute',
    concentration: true,
    description: "You project a phantasmal image of a creature's worst fears. Each creature in a 30-foot cone must succeed on a Wisdom saving throw or drop whatever it is holding and become frightened for the duration. While frightened by this spell, a creature must take the Dash action and move away from you by the safest available route on each of its turns, unless there is nowhere to move.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'fireball',
    name: 'Fireball',
    level: 3,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a tiny ball of bat guano and sulfur' },
    duration: 'Instantaneous',
    concentration: false,
    description: "A bright streak flashes from your pointing finger to a point you choose within range and then blossoms with a low roar into an explosion of flame. Each creature in a 20-foot-radius sphere centered on that point must make a Dexterity saving throw. A target takes 8d6 fire damage on a failed save, or half as much damage on a successful one. The fire spreads around corners. It ignites flammable objects in the area that aren't being worn or carried.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, the damage increases by 1d6 for each slot level above 3rd.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'fly',
    name: 'Fly',
    level: 3,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a wing feather from any bird' },
    duration: '10 minutes',
    concentration: true,
    description: "You touch a willing creature. The target gains a flying speed of 60 feet for the duration. When the spell ends, the target falls if it is still aloft, unless it can stop the fall.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, you can target one additional creature for each slot level above 3rd.",
    classes: ['sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'gaseous-form',
    name: 'Gaseous Form',
    level: 3,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of gauze and a wisp of smoke' },
    duration: '1 hour',
    concentration: true,
    description: "You transform a willing creature you touch, along with everything it's wearing and carrying, into a misty cloud for the duration. The spell ends if the creature drops to 0 hit points. An incorporeal creature isn't affected. While in this form, the target's only method of movement is a flying speed of 10 feet. The target can enter and occupy the space of another creature. The target has resistance to nonmagical damage, and it has advantage on Strength, Dexterity, and Constitution saving throws.",
    classes: ['sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'glyph-of-warding',
    name: 'Glyph of Warding',
    level: 3,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 hour',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'incense and powdered diamond worth at least 200 gp, which the spell consumes', materialCost: 200, materialConsumed: true },
    duration: 'Until dispelled or triggered',
    concentration: false,
    description: "When you cast this spell, you inscribe a glyph that harms other creatures, either upon a surface (such as a table or a section of floor or wall) or within an object that can be closed (such as a book, a scroll, or a treasure chest) to conceal the glyph. If you choose a surface, the glyph can cover an area of the surface no larger than 10 feet in diameter. If you choose an object, that object must remain in its place; if the object is moved more than 10 feet from where you cast this spell, the glyph is broken, and the spell ends without being triggered.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, the damage of an explosive runes glyph increases by 1d8 for each slot level above 3rd. If you create a spell glyph, you can store any spell of up to the same level as the slot you use for the glyph of warding.",
    classes: ['bard', 'cleric', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'haste',
    name: 'Haste',
    level: 3,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a shaving of licorice root' },
    duration: '1 minute',
    concentration: true,
    description: "Choose a willing creature that you can see within range. Until the spell ends, the target's speed is doubled, it gains a +2 bonus to AC, it has advantage on Dexterity saving throws, and it gains an additional action on each of its turns. That action can be used only to take the Attack (one weapon attack only), Dash, Disengage, Hide, or Use an Object action. When the spell ends, the target can't move or take actions until after its next turn, as a wave of lethargy sweeps over it.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'hypnotic-pattern',
    name: 'Hypnotic Pattern',
    level: 3,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: false, somatic: true, material: true, materialDescription: 'a glowing stick of incense or a crystal vial filled with phosphorescent material' },
    duration: '1 minute',
    concentration: true,
    description: "You create a twisting pattern of colors that weaves through the air inside a 30-foot cube within range. The pattern appears for a moment and vanishes. Each creature in the area who sees the pattern must make a Wisdom saving throw. On a failed save, the creature becomes charmed for the duration. While charmed by this spell, the creature is incapacitated and has a speed of 0. The spell ends for an affected creature if it takes any damage or if someone else uses an action to shake the creature out of its stupor.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'lightning-bolt',
    name: 'Lightning Bolt',
    level: 3,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (100-foot line)',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of fur and a rod of amber, crystal, or glass' },
    duration: 'Instantaneous',
    concentration: false,
    description: "A stroke of lightning forming a line 100 feet long and 5 feet wide blasts out from you in a direction you choose. Each creature in the line must make a Dexterity saving throw. A creature takes 8d6 lightning damage on a failed save, or half as much damage on a successful one. The lightning ignites flammable objects in the area that aren't being worn or carried.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, the damage increases by 1d6 for each slot level above 3rd.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'magic-circle',
    name: 'Magic Circle',
    level: 3,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 minute',
    range: '10 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'holy water or powdered silver and iron worth at least 100 gp, which the spell consumes', materialCost: 100, materialConsumed: true },
    duration: '1 hour',
    concentration: false,
    description: "You create a 10-foot-radius, 20-foot-tall cylinder of magical energy centered on a point on the ground that you can see within range. Glowing runes appear wherever the cylinder intersects with the floor or other surface. Choose one or more of the following types of creatures: celestials, elementals, fey, fiends, or undead. The circle affects a creature of the chosen type in several ways including preventing entry and granting disadvantage on attacks.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, the duration increases by 1 hour for each slot level above 3rd.",
    classes: ['cleric', 'paladin', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'major-image',
    name: 'Major Image',
    level: 3,
    school: 'illusion',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a bit of fleece' },
    duration: '10 minutes',
    concentration: true,
    description: "You create the image of an object, a creature, or some other visible phenomenon that is no larger than a 20-foot cube. The image appears at a spot that you can see within range and lasts for the duration. It seems completely real, including sounds, smells, and temperature appropriate to the thing depicted. You can't create sufficient heat or cold to cause damage, a sound loud enough to deal thunder damage or deafen a creature, or a smell that might sicken a creature (like a troglodyte's stench).",
    higherLevel: "When you cast this spell using a spell slot of 6th level or higher, the spell lasts until dispelled, without requiring your concentration.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'mass-healing-word',
    name: 'Mass Healing Word',
    level: 3,
    school: 'evocation',
    ritual: false,
    castingTime: '1 bonus action',
    range: '60 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "As you call out words of restoration, up to six creatures of your choice that you can see within range regain hit points equal to 1d4 + your spellcasting ability modifier. This spell has no effect on undead or constructs.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, the healing increases by 1d4 for each slot level above 3rd.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'meld-into-stone',
    name: 'Meld into Stone',
    level: 3,
    school: 'transmutation',
    ritual: true,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: '8 hours',
    concentration: false,
    description: "You step into a stone object or surface large enough to fully contain your body, melding yourself and all the equipment you carry with the stone for the duration. Using your movement, you step into the stone at a point you can touch. Nothing of your presence remains visible or otherwise detectable by nonmagical senses. While merged with the stone, you can't see what occurs outside it, and any Wisdom (Perception) checks you make to hear sounds outside it are made with disadvantage.",
    classes: ['cleric', 'druid'],
    source: 'srd-5.1',
  },
  {
    id: 'nondetection',
    name: 'Nondetection',
    level: 3,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of diamond dust worth 25 gp sprinkled over the target, which the spell consumes', materialCost: 25, materialConsumed: true },
    duration: '8 hours',
    concentration: false,
    description: "For the duration, you hide a target that you touch from divination magic. The target can be a willing creature or a place or an object no larger than 10 feet in any dimension. The target can't be targeted by any divination magic or perceived through magical scrying sensors.",
    classes: ['bard', 'ranger', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'phantom-steed',
    name: 'Phantom Steed',
    level: 3,
    school: 'illusion',
    ritual: true,
    castingTime: '1 minute',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: false,
    description: "A Large quasi-real, horselike creature appears on the ground in an unoccupied space of your choice within range. You decide the creature's appearance, but it is equipped with a saddle, bit, and bridle. Any of the equipment created by the spell vanishes in a puff of smoke if it is carried more than 10 feet away from the steed. The creature has a speed of 100 feet and can travel 10 miles in an hour, or 13 miles at a fast pace.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'plant-growth',
    name: 'Plant Growth',
    level: 3,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action or 8 hours',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "This spell channels vitality into plants within a specific area. There are two possible uses for the spell, granting either immediate or long-term benefits. If you cast this spell using 1 action, choose a point within range. All normal plants in a 100-foot radius centered on that point become thick and overgrown. If you cast this spell over 8 hours, you enrich the land. All plants in a half-mile radius centered on a point within range become enriched for 1 year.",
    classes: ['bard', 'druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'protection-from-energy',
    name: 'Protection from Energy',
    level: 3,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: true,
    description: "For the duration, the willing creature you touch has resistance to one damage type of your choice: acid, cold, fire, lightning, or thunder.",
    classes: ['cleric', 'druid', 'ranger', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'remove-curse',
    name: 'Remove Curse',
    level: 3,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "At your touch, all curses affecting one creature or object end. If the object is a cursed magic item, its curse remains, but the spell breaks its owner's attunement to the object so it can be removed or discarded.",
    classes: ['cleric', 'paladin', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'revivify',
    name: 'Revivify',
    level: 3,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'diamonds worth 300 gp, which the spell consumes', materialCost: 300, materialConsumed: true },
    duration: 'Instantaneous',
    concentration: false,
    description: "You touch a creature that has died within the last minute. That creature returns to life with 1 hit point. This spell can't return to life a creature that has died of old age, nor can it restore any missing body parts.",
    classes: ['cleric', 'paladin'],
    source: 'srd-5.1',
  },
  {
    id: 'sending',
    name: 'Sending',
    level: 3,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: 'Unlimited',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a short piece of fine copper wire' },
    duration: '1 round',
    concentration: false,
    description: "You send a short message of twenty-five words or less to a creature with which you are familiar. The creature hears the message in its mind, recognizes you as the sender if it knows you, and can answer in a like manner immediately. The spell enables creatures with Intelligence scores of at least 1 to understand the meaning of your message. You can send the message across any distance and even to other planes of existence, but if the target is on a different plane than you, there is a 5 percent chance that the message doesn't arrive.",
    classes: ['bard', 'cleric', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'sleet-storm',
    name: 'Sleet Storm',
    level: 3,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of dust and a few drops of water' },
    duration: '1 minute',
    concentration: true,
    description: "Until the spell ends, freezing rain and sleet fall in a 20-foot-tall cylinder with a 40-foot radius centered on a point you choose within range. The area is heavily obscured, and exposed flames in the area are doused. The ground in the area is covered with slick ice, making it difficult terrain. When a creature enters the spell's area for the first time on a turn or starts its turn there, it must make a Dexterity saving throw. On a failed save, it falls prone.",
    classes: ['druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'slow',
    name: 'Slow',
    level: 3,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a drop of molasses' },
    duration: '1 minute',
    concentration: true,
    description: "You alter time around up to six creatures of your choice in a 40-foot cube within range. Each target must succeed on a Wisdom saving throw or be affected by this spell for the duration. An affected target's speed is halved, it takes a -2 penalty to AC and Dexterity saving throws, and it can't use reactions. On its turn, it can use either an action or a bonus action, not both. Regardless of the creature's abilities or magic items, it can't make more than one melee or ranged attack during its turn.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'speak-with-dead',
    name: 'Speak with Dead',
    level: 3,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: '10 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'burning incense' },
    duration: '10 minutes',
    concentration: false,
    description: "You grant the semblance of life and intelligence to a corpse of your choice within range, allowing it to answer the questions you pose. The corpse must still have a mouth and can't be undead. The spell fails if the corpse was the target of this spell within the last 10 days. Until the spell ends, you can ask the corpse up to five questions. The corpse knows only what it knew in life, including the languages it knew. Answers are usually brief, cryptic, or repetitive, and the corpse is under no compulsion to offer a truthful answer if you are hostile to it or it recognizes you as an enemy.",
    classes: ['bard', 'cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'speak-with-plants',
    name: 'Speak with Plants',
    level: 3,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (30-foot radius)',
    components: { verbal: true, somatic: true, material: false },
    duration: '10 minutes',
    concentration: false,
    description: "You imbue plants within 30 feet of you with limited sentience and animation, giving them the ability to communicate with you and follow your simple commands. You can question plants about events in the spell's area within the past day, gaining information about creatures that have passed, weather, and other circumstances. You can also turn difficult terrain caused by plant growth (such as thickets and undergrowth) into ordinary terrain that lasts for the duration.",
    classes: ['bard', 'druid', 'ranger'],
    source: 'srd-5.1',
  },
  {
    id: 'spirit-guardians',
    name: 'Spirit Guardians',
    level: 3,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (15-foot radius)',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a holy symbol' },
    duration: '10 minutes',
    concentration: true,
    description: "You call forth spirits to protect you. They flit around you to a distance of 15 feet for the duration. If you are good or neutral, their spectral form appears angelic or fey (your choice). If you are evil, they appear fiendish. When you cast this spell, you can designate any number of creatures you can see to be unaffected by it. An affected creature's speed is halved in the area, and when the creature enters the area for the first time on a turn or starts its turn there, it must make a Wisdom saving throw. On a failed save, the creature takes 3d8 radiant damage (if you are good or neutral) or 3d8 necrotic damage (if you are evil). On a successful save, the creature takes half as much damage.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, the damage increases by 1d8 for each slot level above 3rd.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'stinking-cloud',
    name: 'Stinking Cloud',
    level: 3,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '90 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a rotten egg or several skunk cabbage leaves' },
    duration: '1 minute',
    concentration: true,
    description: "You create a 20-foot-radius sphere of yellow, nauseating gas centered on a point within range. The cloud spreads around corners, and its area is heavily obscured. The cloud lingers in the air for the duration. Each creature that is completely within the cloud at the start of its turn must make a Constitution saving throw against poison. On a failed save, the creature spends its action that turn retching and reeling. Creatures that don't need to breathe or are immune to poison automatically succeed on this saving throw.",
    classes: ['bard', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'tongues',
    name: 'Tongues',
    level: 3,
    school: 'divination',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: false, material: true, materialDescription: 'a small clay model of a ziggurat' },
    duration: '1 hour',
    concentration: false,
    description: "This spell grants the creature you touch the ability to understand any spoken language it hears. Moreover, when the target speaks, any creature that knows at least one language and can hear the target understands what it says.",
    classes: ['bard', 'cleric', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'vampiric-touch',
    name: 'Vampiric Touch',
    level: 3,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "The touch of your shadow-wreathed hand can siphon life force from others to heal your wounds. Make a melee spell attack against a creature within your reach. On a hit, the target takes 3d6 necrotic damage, and you regain hit points equal to half the amount of necrotic damage dealt. Until the spell ends, you can make the attack again on each of your turns as an action.",
    higherLevel: "When you cast this spell using a spell slot of 4th level or higher, the damage increases by 1d6 for each slot level above 3rd.",
    classes: ['warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'water-breathing',
    name: 'Water Breathing',
    level: 3,
    school: 'transmutation',
    ritual: true,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a short reed or piece of straw' },
    duration: '24 hours',
    concentration: false,
    description: "This spell grants up to ten willing creatures you can see within range the ability to breathe underwater until the spell ends. Affected creatures also retain their normal mode of respiration.",
    classes: ['druid', 'ranger', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'water-walk',
    name: 'Water Walk',
    level: 3,
    school: 'transmutation',
    ritual: true,
    castingTime: '1 action',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a piece of cork' },
    duration: '1 hour',
    concentration: false,
    description: "This spell grants the ability to move across any liquid surface—such as water, acid, mud, snow, quicksand, or lava—as if it were harmless solid ground (creatures crossing molten lava can still take damage from the heat). Up to ten willing creatures you can see within range gain this ability for the duration. If you target a creature submerged in a liquid, the spell carries the target to the surface of the liquid at a rate of 60 feet per round.",
    classes: ['cleric', 'druid', 'ranger', 'sorcerer'],
    source: 'srd-5.1',
  },
  {
    id: 'wind-wall',
    name: 'Wind Wall',
    level: 3,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '120 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a tiny fan and a feather of exotic origin' },
    duration: '1 minute',
    concentration: true,
    description: "A wall of strong wind rises from the ground at a point you choose within range. You can make the wall up to 50 feet long, 15 feet high, and 1 foot thick. You can shape the wall in any way you choose so long as it makes one continuous path along the ground. The wall lasts for the duration. When the wall appears, each creature within its area must make a Strength saving throw. A creature takes 3d8 bludgeoning damage on a failed save, or half as much damage on a successful one.",
    classes: ['druid', 'ranger'],
    source: 'srd-5.1',
  },
];
